import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { AppConfig } from "../../../api/config/config";
import warning_blue from "../../../assets/images/icons/warning_blue.svg";
import {
  DateInput,
  GenderRadioSelect,
  GeneralButton,
  GeneralInput,
  Modal,
  ProfileImageUploader,
} from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useGetPersonalInfo, usePostData } from "../../../hooks";
import { useToast } from "../../../Services/ToastContext";
import { formatDatePicker, isOlderThan15 } from "../../../utils/utils";
import { EditProfileLayout } from "../component";
import { setUserDetails } from "../../../redux/reducers/registrationSlice";

interface EditPersonalInfoProp {
  onClose?: () => void;
  className?: string;
}

const EditPersonalInfo: React.FC<EditPersonalInfoProp> = ({
  onClose,
  className,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const closeModel = () => setIsOpen(false);

  const userInfoStepSchema = z
    .object({
      File: z.union([z.instanceof(File), z.null()]).optional(),
      FullNameAr: z
        .string()
        .min(1, { message: t("required_field") })
        .transform((val) => val.trim()),
      FullNameEn: z
        .string()
        .min(1, { message: t("required_field") })
        .transform((val) => val.trim()),
      Birthday: z
        .string()
        .min(1, { message: t("required_field") })
        .transform((val) => val.trim())
        .refine((val) => isOlderThan15(val), {
          message: t("bigger_than-15"),
        }),
      Gender: z.string().optional(),
      Bio: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const fullNameAr = data.FullNameAr;
      const fullNameEn = data.FullNameEn;

      if (fullNameAr && fullNameAr.length < 4) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 4,
          type: "string",
          inclusive: true,
          message: t("name_must_be_at_least_4_characters"),
          path: ["FullNameAr"],
        });
      }

      if (fullNameEn && fullNameEn.length < 4) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 4,
          type: "string",
          inclusive: true,
          message: t("name_must_be_at_least_4_characters"),
          path: ["FullNameEn"],
        });
      }
    });

  type UserInfoStep = z.infer<typeof userInfoStepSchema> & {
    FullNameError?: string;
  };

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data } = useGetPersonalInfo();

  const { mutate: updatePersonalInfo, isPending: isUpdating } = usePostData(
    EndPoints.EditPersonalInfo,
  );
  const personalData = data?.Data;

  const methods = useForm<UserInfoStep>({
    defaultValues: {
      File: undefined,
      FullNameAr: "",
      FullNameEn: "",
      Birthday: "",
      Bio: "",
      Gender: undefined,
      FullNameError: "",
    },
    resolver: zodResolver(userInfoStepSchema),
    mode: "onBlur",
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;

  // Watch form values to detect changes
  const watchedValues = watch();

  useEffect(() => {
    if (personalData) {
      reset({
        FullNameAr: personalData.FullNameAr || "",
        FullNameEn: personalData.FullNameEn || "",
        Birthday:
          (personalData.Birthday && formatDatePicker(personalData.Birthday)) ||
          "",
        Gender: personalData.Gender?.trim(),
        File: undefined,
        Bio: personalData.Bio || "",
        FullNameError: "",
      });
      dispatch(
        setUserDetails({
          Gender: personalData.Gender,
        }),
      );
    }
  }, [personalData, reset, dispatch]);

  // Check if name fields have changed
  const hasNameChanged = () => {
    if (!personalData) return false;

    const currentFullNameAr = watchedValues.FullNameAr?.trim() || "";
    const currentFullNameEn = watchedValues.FullNameEn?.trim() || "";
    const originalFullNameAr = personalData.FullNameAr?.trim() || "";
    const originalFullNameEn = personalData.FullNameEn?.trim() || "";

    return (
      currentFullNameAr !== originalFullNameAr ||
      currentFullNameEn !== originalFullNameEn
    );
  };

  const handleSaveClick = () => {
    // Check if form is valid before proceeding
    const isValid = methods.trigger();
    if (!isValid) return;

    // If name has changed, show modal for confirmation
    if (hasNameChanged()) {
      setIsOpen(true);
    } else {
      // Otherwise save directly
      handleSubmit(onSubmit)();
    }
  };

  const onSubmit = (formData: UserInfoStep) => {
    const formDataToSubmit = new FormData();

    if (formData.File instanceof File) {
      formDataToSubmit.append("File", formData.File);
    }

    const fileNotChanged = formData.File === undefined;
    const imageRemoved = formData.File === null;

    formDataToSubmit.append("FileNotChanged", String(fileNotChanged));
    formDataToSubmit.append("ImageRemoved", String(imageRemoved));
    formDataToSubmit.append("FullNameAr", formData.FullNameAr || "");
    formDataToSubmit.append("FullNameEn", formData.FullNameEn || "");

    // Ensure Birthday is a string
    const birthdayValue =
      typeof formData.Birthday === "string" ? formData.Birthday : "";
    formDataToSubmit.append("Birthday", birthdayValue);
    formDataToSubmit.append("Bio", formData.Bio?.replaceAll("\n", " ") || "");
    formDataToSubmit.append("Gender", formData.Gender || "");

    updatePersonalInfo(formDataToSubmit, {
      onError: (error: any) => {
        showToast(
          error.ErrorMessage ||
            error.message ||
            t("update_personal_info_failed"),
          "error",
        );
      },

      onSuccess: (updatedData) => {
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetSpecialty],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [EndPoints.GetPersonalInfo],
        });
        showToast(t("personal_info_updated_successfully"), "success");
        closeModel();
        if (onClose) {
          onClose();
        }
      },
    });
  };

  const location = useLocation();

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };

  return (
    <FormProvider {...methods}>
      <EditProfileLayout
        title={t("edit_profile")}
        onDiscard={onClose ?? handleBack}
        onSave={handleSaveClick}
        isSaving={isUpdating}
      >
        <form
          className="flex w-full flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSaveClick();
          }}
        >
          <ProfileImageUploader
            name="File"
            placeholder={t("profile_photo")}
            defaultImage={
              personalData?.Image
                ? `${AppConfig.SERVER_URL}${personalData.Image}`
                : null
            }
          />

          <GeneralInput
            type="text"
            name="FullNameAr"
            label={t("full_name_in_arabic")}
            placeholder={t("full_name_in_arabic")}
            arabicOnly
            charactersOnly
            aria-invalid={!!errors.FullNameAr}
            aria-describedby="FullNameAr-error"
            maxLength={50}
          />

          <GeneralInput
            type="text"
            name="FullNameEn"
            label={t("full_name_in_english")}
            placeholder={t("full_name_in_english")}
            englishOnly
            charactersOnly
            aria-invalid={!!errors.FullNameEn}
            aria-describedby="FullNameEn-error"
            maxLength={50}
          />

          <DateInput
            label={t("birth_date")}
            dateConstraint="olderThan15"
            name="Birthday"
            aria-describedby="birthday-description"
          />

          <GenderRadioSelect
            name="Gender"
            aria-describedby="gender-description"
          />

          <GeneralInput
            type="textarea"
            name="Bio"
            customStyleClasses="!h-32"
            label={t("bio")}
            placeholder={t("bio")}
            maxLength={1000}
          />
        </form>
      </EditProfileLayout>

      <Modal customStyle="!p-3 md:!p-6" isOpen={isOpen} onClose={closeModel}>
        <div className="flex flex-col gap-8 text-center">
          <div className="flex h-16 items-center">
            <img src={warning_blue} alt="" className="mx-auto h-14 w-14" />
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-bold text-primary-text">
              {t("confirm_changes")}
            </h3>

            <p className="text-sm text-body-text">
              {t("updating_your_name_will_trigger")}
            </p>
          </div>

          <div className="flex items-center justify-end gap-3">
            <GeneralButton
              type="button"
              color="lite"
              title={t("back")}
              disabled={isUpdating}
              onClick={closeModel}
            />

            <GeneralButton
              type="button"
              title={t("confirm")}
              disabled={isUpdating}
              onClick={() => {
                void handleSubmit(onSubmit)();
              }}
            />
          </div>
        </div>
      </Modal>
    </FormProvider>
  );
};

export default EditPersonalInfo;
